import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Package, Tags, Image as ImageIcon, Bell, 
  LogOut, Plus, Edit, Trash2, Save, X, Eye, EyeOff, Upload,
  TrendingUp, MousePointer, FileText, ChevronRight, HelpCircle,
  CheckCircle, AlertCircle, Sparkles, Settings, BarChart3, Wrench
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { id: 'eletricas', name: 'Ferramentas Elétricas' },
  { id: 'manuais', name: 'Ferramentas Manuais' },
  { id: 'jardinagem', name: 'Jardinagem' },
  { id: 'epis', name: 'EPIs' },
  { id: 'acessorios', name: 'Acessórios' },
  { id: 'construcao', name: 'Construção' }
];

// Categorias padrão (fallback)
const defaultCategories = [
  { id: 'eletricas', name: 'Ferramentas Elétricas', slug: 'eletricas' },
  { id: 'manuais', name: 'Ferramentas Manuais', slug: 'manuais' },
  { id: 'jardinagem', name: 'Jardinagem', slug: 'jardinagem' },
  { id: 'epis', name: 'EPIs', slug: 'epis' },
  { id: 'acessorios', name: 'Acessórios', slug: 'acessorios' },
  { id: 'construcao', name: 'Construção', slug: 'construcao' }
];

const bannerPositions = [
  { id: 'home_hero', name: 'Home - Banner Principal', desc: 'Banner grande no topo da página inicial', size: '1920 x 600 px' },
  { id: 'home_middle', name: 'Home - Banner Meio', desc: 'Banners menores no meio da home', size: '600 x 400 px' },
  { id: 'catalog_featured', name: 'Catálogo - Destaque', desc: 'Banner de ofertas no topo do catálogo', size: '1200 x 300 px' },
  { id: 'catalog_side', name: 'Catálogo - Lateral', desc: 'Banners na lateral do catálogo', size: '300 x 600 px' }
];

// Especificações de imagem por tipo
const imageSpecs = {
  product: { width: 800, height: 800, format: 'JPG, PNG ou WebP', maxSize: '5MB', tip: 'Use fundo branco ou transparente para melhor resultado' },
  brand: { width: 400, height: 200, format: 'PNG (fundo transparente) ou SVG', maxSize: '2MB', tip: 'Logo horizontal funciona melhor' },
  banner: { format: 'JPG ou PNG', maxSize: '5MB', tip: 'Use imagens de alta qualidade' },
  popup: { width: 600, height: 400, format: 'JPG ou PNG', maxSize: '3MB', tip: 'A imagem aparece no topo do popup' }
};

// Componente de Dica (Paleta Azul/Vermelho)
const Tip = ({ children }) => (
  <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm text-blue-200 mb-4">
    <HelpCircle size={16} className="mt-0.5 flex-shrink-0 text-blue-400" />
    <span>{children}</span>
  </div>
);

// Componente de Status (Paleta Laranja)
const StatusBadge = ({ active }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
    active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
  }`}>
    {active ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
    {active ? 'Ativo' : 'Inativo'}
  </span>
);

// Componente de Especificação de Imagem
const ImageSpec = ({ type, position }) => {
  const spec = imageSpecs[type];
  const bannerSize = position ? bannerPositions.find(p => p.id === position)?.size : null;
  
  return (
    <div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
      <div className="flex items-start gap-2">
        <AlertCircle size={16} className="text-amber-400 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-amber-200 space-y-1">
          <p className="font-semibold text-amber-300">📐 Especificações da Imagem:</p>
          {bannerSize && <p>• Tamanho ideal: <span className="font-mono bg-slate-700/50 px-1.5 py-0.5 rounded">{bannerSize}</span></p>}
          {spec?.width && !bannerSize && <p>• Tamanho ideal: <span className="font-mono bg-slate-700/50 px-1.5 py-0.5 rounded">{spec.width} x {spec.height} px</span></p>}
          <p>• Formato: <span className="font-mono bg-slate-700/50 px-1.5 py-0.5 rounded">{spec?.format}</span></p>
          <p>• Máximo: <span className="font-mono bg-slate-700/50 px-1.5 py-0.5 rounded">{spec?.maxSize}</span></p>
          {spec?.tip && <p className="text-slate-400 italic">💡 {spec.tip}</p>}
        </div>
      </div>
    </div>
  );
};

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [analytics, setAnalytics] = useState(null);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState(defaultCategories);
  const [banners, setBanners] = useState([]);
  const [popup, setPopup] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  
  // Form states
  const [showProductForm, setShowProductForm] = useState(false);
  const [showBrandForm, setShowBrandForm] = useState(false);
  const [showBannerForm, setShowBannerForm] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  const [productForm, setProductForm] = useState({ name: '', brand: '', category: '', description: '', images: [] });
  const [brandForm, setBrandForm] = useState({ name: '', logo: '', description: '', featured: false });
  const [bannerForm, setBannerForm] = useState({ title: '', image: '', link: '', position: 'home_hero', order: 0 });
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', order: 0 });
  const [popupForm, setPopupForm] = useState({ title: '', description: '', image: '', button_text: 'Ver Oferta', button_link: '' });
  const [settingsForm, setSettingsForm] = useState({ 
    // Seção Parceiro Destaque
    partner_section_logo: 'https://cdn.worldvectorlogo.com/logos/stihl.svg',
    partner_section_title: 'Distribuidor Autorizado',
    partner_section_brand: 'STIHL',
    partner_section_description: '',
    partner_section_indicator1_title: '100%',
    partner_section_indicator1_subtitle: 'Produtos Originais',
    partner_section_indicator2_title: 'Garantia',
    partner_section_indicator2_subtitle: 'Oficial de Fábrica',
    partner_section_button_text: 'Ver Produtos STIHL',
    partner_section_button_link: '/catalogo?marca=STIHL',
    // Trust indicators
    hero_indicator1_title: 'STIHL',
    hero_indicator1_subtitle: 'Distribuidor Oficial',
    hero_indicator1_logo: '',
    hero_indicator2_title: '8+',
    hero_indicator2_subtitle: 'Marcas Líderes',
    hero_indicator3_title: '100%',
    hero_indicator3_subtitle: 'Produtos Originais',
    hero_indicator4_title: 'Garantia',
    hero_indicator4_subtitle: 'De Fábrica'
  });

  const checkAuth = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) navigate('/admin-painel-gestao');
  }, [navigate]);

  const getAuthHeaders = useCallback(() => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
  }), []);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin-painel-gestao');
  };

  // Fetch functions
  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/analytics?days=30`, getAuthHeaders());
      setAnalytics(response.data);
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
    }
  }, [getAuthHeaders]);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/products`, getAuthHeaders());
      setProducts(response.data);
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
    }
  }, [getAuthHeaders]);

  const fetchBrands = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/brands`, getAuthHeaders());
      setBrands(response.data);
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
    }
  }, [getAuthHeaders]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/categories`, getAuthHeaders());
      if (response.data && response.data.length > 0) {
        setCategories(response.data);
      }
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
    }
  }, [getAuthHeaders]);

  const fetchBanners = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/banners`, getAuthHeaders());
      setBanners(response.data);
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
    }
  }, [getAuthHeaders]);

  const fetchPopup = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/popup`, getAuthHeaders());
      setPopup(response.data);
      if (response.data) {
        setPopupForm({
          title: response.data.title || '',
          description: response.data.description || '',
          image: response.data.image || '',
          button_text: response.data.button_text || 'Ver Oferta',
          button_link: response.data.button_link || ''
        });
      }
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
    }
  }, [getAuthHeaders]);

  const fetchSiteSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/settings`, getAuthHeaders());
      setSiteSettings(response.data);
      if (response.data) {
        setSettingsForm(prev => ({
          ...prev,
          ...response.data
        }));
      }
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    checkAuth();
    fetchProducts();
    fetchBrands();
    fetchCategories();
  }, [checkAuth, fetchProducts, fetchBrands, fetchCategories]);

  useEffect(() => {
    if (activeTab === 'dashboard') fetchAnalytics();
    if (activeTab === 'products') fetchProducts();
    if (activeTab === 'brands') fetchBrands();
    if (activeTab === 'categories') fetchCategories();
    if (activeTab === 'banners') fetchBanners();
    if (activeTab === 'popup') fetchPopup();
    if (activeTab === 'settings') fetchSiteSettings();
  }, [activeTab, fetchAnalytics, fetchProducts, fetchBrands, fetchBanners, fetchPopup, fetchSiteSettings]);

  // Image upload
  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validar tamanho (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Arquivo muito grande", description: "Máximo 5MB", variant: "destructive" });
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/upload-image`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = `${BACKEND_URL}${response.data.image_url}`;
      
      if (type === 'product') {
        setProductForm({ ...productForm, images: [...productForm.images, imageUrl] });
      } else if (type === 'brand') {
        setBrandForm({ ...brandForm, logo: imageUrl });
      } else if (type === 'banner') {
        setBannerForm({ ...bannerForm, image: imageUrl });
      } else if (type === 'popup') {
        setPopupForm({ ...popupForm, image: imageUrl });
      } else if (type === 'settings') {
        setSettingsForm({ ...settingsForm, partner_section_logo: imageUrl });
      }
      
      toast({ title: "✓ Imagem enviada!", description: "Upload realizado com sucesso." });
    } catch (error) {
      toast({ title: "Erro no upload", description: "Tente novamente", variant: "destructive" });
    }
    setLoading(false);
  };

  // Site Settings
  const saveSiteSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/settings`, settingsForm, getAuthHeaders());
      toast({ title: "✓ Configurações salvas!" });
      fetchSiteSettings();
    } catch (error) {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    }
    setLoading(false);
  };

  // CRUD Products
  const saveProduct = async (e) => {
    e.preventDefault();
    if (!productForm.name || !productForm.brand || !productForm.category) {
      toast({ title: "Preencha todos os campos obrigatórios", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/admin/products/${editingItem.id}`, productForm, getAuthHeaders());
        toast({ title: "✓ Produto atualizado!" });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/products`, productForm, getAuthHeaders());
        toast({ title: "✓ Produto criado!" });
      }
      setShowProductForm(false);
      setEditingItem(null);
      setProductForm({ name: '', brand: '', category: '', description: '', images: [] });
      fetchProducts();
    } catch (error) {
      toast({ title: "Erro ao salvar", description: error.response?.data?.detail || "Tente novamente", variant: "destructive" });
    }
    setLoading(false);
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este produto?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/products/${id}`, getAuthHeaders());
      toast({ title: "✓ Produto excluído!" });
      fetchProducts();
    } catch (error) {
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  // CRUD Brands
  const saveBrand = async (e) => {
    e.preventDefault();
    if (!brandForm.name || !brandForm.logo) {
      toast({ title: "Nome e logo são obrigatórios", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/admin/brands/${editingItem.id}`, brandForm, getAuthHeaders());
        toast({ title: "✓ Marca atualizada!" });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/brands`, brandForm, getAuthHeaders());
        toast({ title: "✓ Marca criada!" });
      }
      setShowBrandForm(false);
      setEditingItem(null);
      setBrandForm({ name: '', logo: '', description: '', featured: false });
      fetchBrands();
    } catch (error) {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    }
    setLoading(false);
  };

  const deleteBrand = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta marca?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/brands/${id}`, getAuthHeaders());
      toast({ title: "✓ Marca excluída!" });
      fetchBrands();
    } catch (error) {
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  // CRUD Categories
  const saveCategory = async (e) => {
    e.preventDefault();
    if (!categoryForm.name) {
      toast({ title: "Nome da categoria é obrigatório", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/admin/categories/${editingItem.id}`, categoryForm, getAuthHeaders());
        toast({ title: "✓ Categoria atualizada!" });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/categories`, categoryForm, getAuthHeaders());
        toast({ title: "✓ Categoria criada!" });
      }
      setShowCategoryForm(false);
      setEditingItem(null);
      setCategoryForm({ name: '', description: '', order: 0 });
      fetchCategories();
    } catch (error) {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    }
    setLoading(false);
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta categoria?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/categories/${id}`, getAuthHeaders());
      toast({ title: "✓ Categoria excluída!" });
      fetchCategories();
    } catch (error) {
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  // CRUD Banners
  const saveBanner = async (e) => {
    e.preventDefault();
    if (!bannerForm.title || !bannerForm.image) {
      toast({ title: "Título e imagem são obrigatórios", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/admin/banners/${editingItem.id}`, bannerForm, getAuthHeaders());
        toast({ title: "✓ Banner atualizado!" });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/banners`, bannerForm, getAuthHeaders());
        toast({ title: "✓ Banner criado!" });
      }
      setShowBannerForm(false);
      setEditingItem(null);
      setBannerForm({ title: '', image: '', link: '', position: 'home_hero', order: 0 });
      fetchBanners();
    } catch (error) {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    }
    setLoading(false);
  };

  const deleteBanner = async (id) => {
    if (!window.confirm('Excluir este banner?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/banners/${id}`, getAuthHeaders());
      toast({ title: "✓ Banner excluído!" });
      fetchBanners();
    } catch (error) {
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  const toggleBanner = async (banner) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/banners/${banner.id}`, { is_active: !banner.is_active }, getAuthHeaders());
      toast({ title: banner.is_active ? "Banner desativado" : "Banner ativado" });
      fetchBanners();
    } catch (error) {
      toast({ title: "Erro ao atualizar", variant: "destructive" });
    }
  };

  // Popup
  const savePopup = async (e) => {
    e.preventDefault();
    if (!popupForm.title) {
      toast({ title: "Título é obrigatório", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      if (popup) {
        await axios.put(`${BACKEND_URL}/api/admin/popup`, popupForm, getAuthHeaders());
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/popup`, popupForm, getAuthHeaders());
      }
      toast({ title: "✓ Popup salvo!" });
      fetchPopup();
    } catch (error) {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    }
    setLoading(false);
  };

  const togglePopup = async () => {
    try {
      const response = await axios.put(`${BACKEND_URL}/api/admin/popup/toggle`, {}, getAuthHeaders());
      toast({ title: response.data.is_active ? "✓ Popup ativado!" : "Popup desativado" });
      fetchPopup();
    } catch (error) {
      toast({ title: "Configure o popup primeiro", variant: "destructive" });
    }
  };

  // Menu items
  const menuItems = [
    { id: 'dashboard', icon: BarChart3, label: 'Dashboard', desc: 'Visão geral' },
    { id: 'products', icon: Package, label: 'Produtos', desc: `${products.length} itens` },
    { id: 'categories', icon: LayoutDashboard, label: 'Categorias', desc: `${categories.length} categorias` },
    { id: 'brands', icon: Tags, label: 'Marcas', desc: `${brands.length} marcas` },
    { id: 'banners', icon: ImageIcon, label: 'Banners', desc: 'Publicidade' },
    { id: 'popup', icon: Bell, label: 'Popup', desc: popup?.is_active ? 'Ativo' : 'Inativo' },
    { id: 'settings', icon: Sparkles, label: 'Parceiro Destaque', desc: 'Seção da Home' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex">
      {/* Sidebar Premium */}
      <div className="w-72 bg-gradient-to-b from-slate-800 to-slate-900 border-r border-slate-700/50 flex flex-col shadow-2xl">
        {/* Logo */}
        <div className="p-6 border-b border-slate-700/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
              <Wrench size={20} className="text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base font-bold text-white truncate">Casa das Ferramentas</h1>
              <p className="text-slate-400 text-xs">Painel de Gestão</p>
            </div>
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
                activeTab === item.id 
                  ? 'bg-gradient-to-r from-red-500/20 to-red-600/20 border border-red-500/30 text-white shadow-lg' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <div className={`p-2 rounded-lg ${activeTab === item.id ? 'bg-red-500/20' : 'bg-slate-700/50 group-hover:bg-slate-600/50'}`}>
                <item.icon size={18} className={activeTab === item.id ? 'text-red-400' : ''} />
              </div>
              <div className="flex-1 text-left">
                <span className="font-medium block">{item.label}</span>
                <span className="text-xs text-slate-500">{item.desc}</span>
              </div>
              {activeTab === item.id && <ChevronRight size={16} className="text-red-400" />}
            </button>
          ))}
        </nav>
        
        {/* User & Logout */}
        <div className="p-4 border-t border-slate-700/50">
          <div className="bg-slate-800/50 rounded-xl p-4 mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center text-white font-bold">
                A
              </div>
              <div>
                <p className="text-white text-sm font-medium">Admin</p>
                <p className="text-slate-400 text-xs">admin@cdf.com</p>
              </div>
            </div>
          </div>
          <Button onClick={handleLogout} variant="ghost" className="w-full justify-start text-slate-400 hover:text-red-400 hover:bg-red-500/10">
            <LogOut size={18} className="mr-3" /> Sair do Painel
          </Button>
          
          {/* Suporte GF */}
          <div className="mt-4 pt-4 border-t border-slate-700/50">
            <p className="text-xs text-slate-500 text-center">
              Suporte técnico:{' '}
              <a 
                href="https://instagram.com/gfcomunicacaoo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-red-400 hover:text-red-300"
              >
                @gfcomunicacaoo
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          {/* DASHBOARD */}
          {activeTab === 'dashboard' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">Dashboard</h2>
                <p className="text-slate-400">Acompanhe o desempenho do seu site</p>
              </div>
              
              <Tip>
                Os dados são atualizados em tempo real. Clique em "Produtos" no menu para adicionar novos itens ao catálogo.
              </Tip>
              
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="bg-gradient-to-br from-orange-500 to-orange-600 border-0 shadow-xl shadow-orange-500/20">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-orange-100 text-sm font-medium">Visualizações</p>
                        <p className="text-4xl font-bold text-white mt-1">{analytics?.total_views || 0}</p>
                        <p className="text-blue-200 text-xs mt-2">últimos 30 dias</p>
                      </div>
                      <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                        <Eye size={28} className="text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 border-0 shadow-xl shadow-emerald-500/20">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-emerald-100 text-sm font-medium">Contatos</p>
                        <p className="text-4xl font-bold text-white mt-1">{analytics?.total_contacts || 0}</p>
                        <p className="text-emerald-200 text-xs mt-2">cliques em WhatsApp/Email</p>
                      </div>
                      <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                        <MousePointer size={28} className="text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-purple-500 to-purple-600 border-0 shadow-xl shadow-purple-500/20">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm font-medium">Orçamentos</p>
                        <p className="text-4xl font-bold text-white mt-1">{analytics?.total_quotes || 0}</p>
                        <p className="text-purple-200 text-xs mt-2">solicitações enviadas</p>
                      </div>
                      <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                        <FileText size={28} className="text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-orange-500 to-orange-600 border-0 shadow-xl shadow-orange-500/20">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-orange-100 text-sm font-medium">Produtos</p>
                        <p className="text-4xl font-bold text-white mt-1">{products.length}</p>
                        <p className="text-orange-200 text-xs mt-2">cadastrados no site</p>
                      </div>
                      <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                        <Package size={28} className="text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Charts/Lists */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-6">
                      <TrendingUp className="text-blue-400" size={20} />
                      <h3 className="text-lg font-bold text-white">Produtos Mais Vistos</h3>
                    </div>
                    {analytics?.top_products?.length > 0 ? (
                      <div className="space-y-3">
                        {analytics.top_products.slice(0, 5).map((p, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-xl">
                            <div className="flex items-center gap-3">
                              <span className="w-6 h-6 bg-blue-500/20 text-blue-400 rounded-full flex items-center justify-center text-xs font-bold">
                                {i + 1}
                              </span>
                              <span className="text-white text-sm">{p.product_name}</span>
                            </div>
                            <span className="text-blue-400 text-sm font-medium">{p.views} views</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Sparkles className="mx-auto text-slate-600 mb-3" size={40} />
                        <p className="text-slate-400">Os dados aparecerão aqui conforme os visitantes navegam</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-6">
                      <MousePointer className="text-emerald-400" size={20} />
                      <h3 className="text-lg font-bold text-white">Contatos por Tipo</h3>
                    </div>
                    {analytics?.contacts_by_type && Object.keys(analytics.contacts_by_type).length > 0 ? (
                      <div className="space-y-3">
                        {Object.entries(analytics.contacts_by_type).map(([type, count]) => {
                          const typeConfig = {
                            whatsapp: { label: 'WhatsApp', color: 'text-green-400', bg: 'bg-green-500/20' },
                            email: { label: 'E-mail', color: 'text-blue-400', bg: 'bg-blue-500/20' },
                            orcamento: { label: 'Orçamento', color: 'text-purple-400', bg: 'bg-purple-500/20' },
                          };
                          const config = typeConfig[type] || { label: type, color: 'text-slate-400', bg: 'bg-slate-500/20' };
                          return (
                            <div key={type} className={`flex items-center justify-between p-3 ${config.bg} rounded-xl`}>
                              <span className={`${config.color} text-sm font-medium`}>{config.label}</span>
                              <span className={`${config.color} text-sm font-bold`}>{count} cliques</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Sparkles className="mx-auto text-slate-600 mb-3" size={40} />
                        <p className="text-slate-400">Aguardando interações dos visitantes</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* PRODUCTS */}
          {activeTab === 'products' && (
            <div className="space-y-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-3xl font-bold text-white mb-2">Produtos</h2>
                  <p className="text-slate-400">Gerencie o catálogo de produtos do site</p>
                </div>
                <Button onClick={() => { setShowProductForm(true); setEditingItem(null); setProductForm({ name: '', brand: '', category: '', description: '', images: [] }); }}
                  className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 shadow-lg">
                  <Plus size={18} className="mr-2" /> Novo Produto
                </Button>
              </div>

              <Tip>
                Para adicionar um produto: clique em "Novo Produto", preencha o nome, escolha a marca e categoria, e faça upload de uma imagem. Os produtos aparecem automaticamente no catálogo do site.
              </Tip>

              {showProductForm && (
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold text-white">{editingItem ? '✏️ Editar Produto' : '➕ Novo Produto'}</h3>
                      <Button variant="ghost" size="sm" onClick={() => setShowProductForm(false)} className="text-slate-400 hover:text-white">
                        <X size={20} />
                      </Button>
                    </div>
                    <form onSubmit={saveProduct} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Nome do Produto *</label>
                          <Input 
                            value={productForm.name} 
                            onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} 
                            placeholder="Ex: Furadeira de Impacto 750W"
                            className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                            required 
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Marca *</label>
                          <select 
                            value={productForm.brand} 
                            onChange={(e) => setProductForm({ ...productForm, brand: e.target.value })}
                            className="w-full h-10 px-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white"
                            required
                          >
                            <option value="">Selecione a marca</option>
                            {brands.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
                          </select>
                          {brands.length === 0 && <p className="text-amber-400 text-xs mt-1">⚠️ Cadastre marcas primeiro</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Categoria *</label>
                          <select 
                            value={productForm.category} 
                            onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                            className="w-full h-10 px-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white"
                            required
                          >
                            <option value="">Selecione a categoria</option>
                            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Imagem do Produto</label>
                          <Input 
                            type="file" 
                            accept="image/png,image/jpeg,image/webp" 
                            onChange={(e) => handleImageUpload(e, 'product')} 
                            className="bg-slate-700/50 border-slate-600 text-white file:bg-slate-600 file:text-white file:border-0"
                          />
                          <ImageSpec type="product" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Descrição</label>
                        <Textarea 
                          value={productForm.description} 
                          onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} 
                          rows={3} 
                          placeholder="Descreva as características do produto..."
                          className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                        />
                      </div>
                      {productForm.images.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Imagens adicionadas</label>
                          <div className="flex gap-3 flex-wrap">
                            {productForm.images.map((img, i) => (
                              <div key={i} className="relative group">
                                <img src={img} alt="" className="w-24 h-24 object-cover rounded-xl border-2 border-slate-600" />
                                <button 
                                  type="button" 
                                  onClick={() => setProductForm({ ...productForm, images: productForm.images.filter((_, idx) => idx !== i) })}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex gap-3">
                        <Button type="submit" disabled={loading} className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700">
                          <Save size={18} className="mr-2" /> {loading ? 'Salvando...' : 'Salvar Produto'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowProductForm(false)} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                          Cancelar
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {products.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products.map((product) => (
                    <Card key={product.id} className="bg-slate-800/50 border-slate-700/50 overflow-hidden group hover:border-slate-600 transition-all">
                      <div className="h-40 bg-slate-700/50 relative overflow-hidden">
                        {product.images?.[0] ? (
                          <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon size={40} className="text-slate-600" />
                          </div>
                        )}
                        <div className="absolute top-2 right-2">
                          <StatusBadge active={product.is_active} />
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <h4 className="font-bold text-white truncate">{product.name}</h4>
                        <p className="text-slate-400 text-sm">{product.brand} • {categories.find(c => c.id === product.category)?.name || product.category}</p>
                        <div className="flex gap-2 mt-4">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                            onClick={() => { 
                              setEditingItem(product); 
                              setProductForm({ 
                                name: product.name, 
                                brand: product.brand, 
                                category: product.category, 
                                description: product.description || '', 
                                images: product.images || [] 
                              }); 
                              setShowProductForm(true); 
                            }}
                          >
                            <Edit size={14} className="mr-1" /> Editar
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            onClick={() => deleteProduct(product.id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-12 text-center">
                    <Package className="mx-auto text-slate-600 mb-4" size={48} />
                    <h3 className="text-xl font-bold text-white mb-2">Nenhum produto cadastrado</h3>
                    <p className="text-slate-400 mb-6">Comece adicionando seu primeiro produto ao catálogo</p>
                    <Button onClick={() => setShowProductForm(true)} className="bg-gradient-to-r from-red-500 to-orange-500">
                      <Plus size={18} className="mr-2" /> Adicionar Primeiro Produto
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* CATEGORIES */}
          {activeTab === 'categories' && (
            <div className="space-y-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-3xl font-bold text-white mb-2">Categorias</h2>
                  <p className="text-slate-400">Gerencie as categorias de produtos</p>
                </div>
                <Button onClick={() => { setShowCategoryForm(true); setEditingItem(null); setCategoryForm({ name: '', description: '', order: 0 }); }}
                  className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 shadow-lg">
                  <Plus size={18} className="mr-2" /> Nova Categoria
                </Button>
              </div>

              <Tip>
                As categorias organizam os produtos no catálogo. Use a ordem para definir a posição no filtro (menor número = aparece primeiro).
              </Tip>

              {showCategoryForm && (
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold text-white">{editingItem ? '✏️ Editar Categoria' : '➕ Nova Categoria'}</h3>
                      <Button variant="ghost" size="sm" onClick={() => setShowCategoryForm(false)} className="text-slate-400 hover:text-white">
                        <X size={20} />
                      </Button>
                    </div>
                    <form onSubmit={saveCategory} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Nome da Categoria *</label>
                          <Input 
                            value={categoryForm.name} 
                            onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} 
                            placeholder="Ex: Ferramentas Elétricas"
                            className="bg-slate-700/50 border-slate-600 text-white"
                            required 
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Ordem de Exibição</label>
                          <Input 
                            type="number"
                            value={categoryForm.order} 
                            onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 0 })} 
                            placeholder="0"
                            className="bg-slate-700/50 border-slate-600 text-white"
                          />
                          <p className="text-slate-500 text-xs mt-1">Menor número = aparece primeiro</p>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Descrição (opcional)</label>
                        <Textarea 
                          value={categoryForm.description} 
                          onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} 
                          rows={2} 
                          placeholder="Uma breve descrição da categoria..."
                          className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                        />
                      </div>
                      <div className="flex gap-3">
                        <Button type="submit" disabled={loading} className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700">
                          <Save size={18} className="mr-2" /> {loading ? 'Salvando...' : 'Salvar Categoria'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowCategoryForm(false)} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                          Cancelar
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {categories.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categories.map((category) => (
                    <Card key={category.id} className="bg-slate-800/50 border-slate-700/50 hover:border-slate-600 transition-all">
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                                <LayoutDashboard size={20} className="text-white" />
                              </div>
                              <div>
                                <h4 className="font-bold text-white">{category.name}</h4>
                                <p className="text-slate-500 text-xs">Ordem: {category.order || 0}</p>
                              </div>
                            </div>
                            {category.description && (
                              <p className="text-slate-400 text-sm mt-2 line-clamp-2">{category.description}</p>
                            )}
                          </div>
                          <StatusBadge active={category.is_active !== false} />
                        </div>
                        <div className="flex gap-2 mt-4 pt-4 border-t border-slate-700/50">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                            onClick={() => { 
                              setEditingItem(category); 
                              setCategoryForm({ name: category.name, description: category.description || '', order: category.order || 0 }); 
                              setShowCategoryForm(true); 
                            }}
                          >
                            <Edit size={14} className="mr-1" /> Editar
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            onClick={() => deleteCategory(category.id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="bg-slate-800/50 border-slate-700/50 border-dashed">
                  <CardContent className="p-12 text-center">
                    <LayoutDashboard size={48} className="mx-auto text-slate-600 mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">Nenhuma categoria cadastrada</h3>
                    <p className="text-slate-400 mb-6">Crie categorias para organizar seus produtos no catálogo.</p>
                    <Button onClick={() => setShowCategoryForm(true)} className="bg-gradient-to-r from-red-500 to-orange-500">
                      <Plus size={18} className="mr-2" /> Criar Primeira Categoria
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* BRANDS */}
          {activeTab === 'brands' && (
            <div className="space-y-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-3xl font-bold text-white mb-2">Marcas</h2>
                  <p className="text-slate-400">Gerencie as marcas disponíveis no site</p>
                </div>
                <Button onClick={() => { setShowBrandForm(true); setEditingItem(null); setBrandForm({ name: '', logo: '', description: '', featured: false }); }}
                  className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 shadow-lg">
                  <Plus size={18} className="mr-2" /> Nova Marca
                </Button>
              </div>

              <Tip>
                As marcas marcadas como "Destaque" aparecem em tamanho maior na página inicial. 
                <strong className="text-blue-400"> Tamanho ideal da logo: 400 x 200 pixels</strong>. 
                Use PNG com fundo transparente ou SVG para melhor resultado.
              </Tip>

              {showBrandForm && (
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold text-white">{editingItem ? '✏️ Editar Marca' : '➕ Nova Marca'}</h3>
                      <Button variant="ghost" size="sm" onClick={() => setShowBrandForm(false)} className="text-slate-400 hover:text-white">
                        <X size={20} />
                      </Button>
                    </div>
                    <form onSubmit={saveBrand} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Nome da Marca *</label>
                          <Input 
                            value={brandForm.name} 
                            onChange={(e) => setBrandForm({ ...brandForm, name: e.target.value })} 
                            placeholder="Ex: Makita"
                            className="bg-slate-700/50 border-slate-600 text-white"
                            required 
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Logo da Marca *</label>
                          <Input 
                            type="file" 
                            accept="image/png,image/svg+xml,image/jpeg" 
                            onChange={(e) => handleImageUpload(e, 'brand')} 
                            className="bg-slate-700/50 border-slate-600 text-white file:bg-slate-600 file:text-white file:border-0"
                          />
                          <ImageSpec type="brand" />
                          {!brandForm.logo && (
                            <div className="mt-2">
                              <Input 
                                placeholder="Ou cole a URL da logo"
                                value={brandForm.logo}
                                onChange={(e) => setBrandForm({ ...brandForm, logo: e.target.value })}
                                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      {brandForm.logo && (
                        <div className="flex items-center gap-4 p-4 bg-slate-700/30 rounded-xl">
                          <img src={brandForm.logo} alt="Preview" className="h-16 w-auto object-contain bg-white p-2 rounded-lg" />
                          <Button type="button" variant="outline" size="sm" onClick={() => setBrandForm({ ...brandForm, logo: '' })} className="border-slate-600 text-slate-300">
                            Remover
                          </Button>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Descrição (opcional)</label>
                        <Textarea 
                          value={brandForm.description} 
                          onChange={(e) => setBrandForm({ ...brandForm, description: e.target.value })} 
                          rows={2}
                          placeholder="Breve descrição da marca..."
                          className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                        />
                      </div>
                      <div className="flex items-center gap-3 p-4 bg-slate-700/30 rounded-xl">
                        <Switch checked={brandForm.featured} onCheckedChange={(v) => setBrandForm({ ...brandForm, featured: v })} />
                        <div>
                          <label className="text-white font-medium">Marca em Destaque</label>
                          <p className="text-slate-400 text-sm">Aparece maior na página inicial</p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <Button type="submit" disabled={loading} className="bg-gradient-to-r from-blue-500 to-blue-600">
                          <Save size={18} className="mr-2" /> {loading ? 'Salvando...' : 'Salvar Marca'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowBrandForm(false)} className="border-slate-600 text-slate-300">
                          Cancelar
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                {brands.map((brand) => (
                  <Card key={brand.id} className={`bg-slate-800/50 border-slate-700/50 overflow-hidden group hover:border-slate-600 transition-all ${brand.featured ? 'ring-2 ring-amber-500/50' : ''}`}>
                    <div className="h-24 bg-white p-4 flex items-center justify-center">
                      {brand.logo ? (
                        <img src={brand.logo} alt={brand.name} className="max-h-full max-w-full object-contain" />
                      ) : (
                        <Tags size={32} className="text-slate-300" />
                      )}
                    </div>
                    <CardContent className="p-3">
                      <h4 className="font-bold text-white text-sm truncate">{brand.name}</h4>
                      {brand.featured && <span className="text-amber-400 text-xs">★ Destaque</span>}
                      <div className="flex gap-1 mt-2">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-8 w-8 p-0 text-slate-400 hover:text-white hover:bg-slate-700"
                          onClick={() => { 
                            setEditingItem(brand); 
                            setBrandForm({ 
                              name: brand.name, 
                              logo: brand.logo, 
                              description: brand.description || '', 
                              featured: brand.featured 
                            }); 
                            setShowBrandForm(true); 
                          }}
                        >
                          <Edit size={14} />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          onClick={() => deleteBrand(brand.id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {brands.length === 0 && (
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-12 text-center">
                    <Tags className="mx-auto text-slate-600 mb-4" size={48} />
                    <p className="text-slate-400">Nenhuma marca cadastrada ainda</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* BANNERS */}
          {activeTab === 'banners' && (
            <div className="space-y-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-3xl font-bold text-white mb-2">Banners</h2>
                  <p className="text-slate-400">Gerencie os banners de publicidade do site</p>
                </div>
                <Button onClick={() => { setShowBannerForm(true); setEditingItem(null); setBannerForm({ title: '', image: '', link: '', position: 'home_hero', order: 0 }); }}
                  className="bg-gradient-to-r from-red-500 to-orange-500 shadow-lg">
                  <Plus size={18} className="mr-2" /> Novo Banner
                </Button>
              </div>

              <Tip>
                Use banners para destacar promoções, parcerias ou ofertas especiais. 
                <strong className="text-blue-400"> Cada posição tem um tamanho específico</strong> - veja as medidas ao selecionar a posição.
                Você pode ativar/desativar cada banner sem precisar excluí-lo.
              </Tip>

              {showBannerForm && (
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold text-white">{editingItem ? '✏️ Editar Banner' : '➕ Novo Banner'}</h3>
                      <Button variant="ghost" size="sm" onClick={() => setShowBannerForm(false)} className="text-slate-400"><X size={20} /></Button>
                    </div>
                    <form onSubmit={saveBanner} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Título *</label>
                          <Input value={bannerForm.title} onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })} className="bg-slate-700/50 border-slate-600 text-white" required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Posição *</label>
                          <select value={bannerForm.position} onChange={(e) => setBannerForm({ ...bannerForm, position: e.target.value })} className="w-full h-10 px-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white" required>
                            {bannerPositions.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.size})</option>)}
                          </select>
                          <p className="text-slate-500 text-xs mt-1">{bannerPositions.find(p => p.id === bannerForm.position)?.desc}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Imagem *</label>
                          <Input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => handleImageUpload(e, 'banner')} className="bg-slate-700/50 border-slate-600 text-white file:bg-slate-600 file:text-white file:border-0" />
                          <ImageSpec type="banner" position={bannerForm.position} />
                          {!bannerForm.image && <Input className="mt-2 bg-slate-700/50 border-slate-600 text-white" placeholder="Ou cole URL" onChange={(e) => setBannerForm({ ...bannerForm, image: e.target.value })} />}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Link (ao clicar)</label>
                          <Input value={bannerForm.link} onChange={(e) => setBannerForm({ ...bannerForm, link: e.target.value })} placeholder="/catalogo ou https://..." className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500" />
                        </div>
                      </div>
                      {bannerForm.image && (
                        <div className="relative inline-block">
                          <img src={bannerForm.image} alt="Preview" className="max-h-40 rounded-xl border border-slate-600" />
                          <button type="button" onClick={() => setBannerForm({ ...bannerForm, image: '' })} className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5"><X size={14} /></button>
                        </div>
                      )}
                      <Button type="submit" disabled={loading} className="bg-gradient-to-r from-blue-500 to-blue-600">
                        <Save size={18} className="mr-2" /> {loading ? 'Salvando...' : 'Salvar Banner'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}

              <div className="space-y-6">
                {bannerPositions.map((position) => (
                  <div key={position.id}>
                    <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                      <ImageIcon size={18} className="text-slate-400" />
                      {position.name}
                      <span className="text-slate-500 text-sm font-normal">- {position.desc}</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {banners.filter(b => b.position === position.id).map((banner) => (
                        <Card key={banner.id} className={`bg-slate-800/50 border-slate-700/50 overflow-hidden ${!banner.is_active ? 'opacity-50' : ''}`}>
                          <div className="h-32 bg-slate-700/50">
                            {banner.image ? <img src={banner.image} alt={banner.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><ImageIcon size={32} className="text-slate-600" /></div>}
                          </div>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-bold text-white truncate">{banner.title}</h4>
                              <Switch checked={banner.is_active} onCheckedChange={() => toggleBanner(banner)} />
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" className="border-slate-600 text-slate-300" onClick={() => { setEditingItem(banner); setBannerForm({ title: banner.title, image: banner.image, link: banner.link || '', position: banner.position, order: banner.order }); setShowBannerForm(true); }}>
                                <Edit size={14} />
                              </Button>
                              <Button size="sm" variant="outline" className="border-red-500/50 text-red-400" onClick={() => deleteBanner(banner.id)}>
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                    {banners.filter(b => b.position === position.id).length === 0 && (
                      <p className="text-slate-500 text-sm py-4 pl-4 border-l-2 border-slate-700">Nenhum banner nesta posição</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* POPUP */}
          {activeTab === 'popup' && (
            <div className="space-y-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-3xl font-bold text-white mb-2">Popup de Oferta</h2>
                  <p className="text-slate-400">Configure um popup promocional para os visitantes</p>
                </div>
                <div className="flex items-center gap-4">
                  <StatusBadge active={popup?.is_active} />
                  <Button onClick={togglePopup} variant={popup?.is_active ? 'destructive' : 'default'} className={!popup?.is_active ? 'bg-gradient-to-r from-green-500 to-emerald-500' : ''}>
                    {popup?.is_active ? <><EyeOff size={18} className="mr-2" /> Desativar</> : <><Eye size={18} className="mr-2" /> Ativar</>}
                  </Button>
                </div>
              </div>

              <Tip>
                O popup aparece uma vez por sessão para cada visitante. Use para ofertas especiais, promoções ou avisos importantes.
              </Tip>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-white mb-6">Configurar Popup</h3>
                    <form onSubmit={savePopup} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Imagem do Popup *</label>
                        <Input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => handleImageUpload(e, 'popup')} className="bg-slate-700/50 border-slate-600 text-white file:bg-slate-600 file:text-white file:border-0" />
                        <ImageSpec type="popup" />
                        {popupForm.image && (
                          <div className="mt-2 relative inline-block">
                            <img src={popupForm.image} alt="Preview" className="max-h-40 rounded-lg" />
                            <button type="button" onClick={() => setPopupForm({ ...popupForm, image: '' })} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"><X size={12} /></button>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Link ao Clicar na Imagem</label>
                        <Input value={popupForm.button_link} onChange={(e) => setPopupForm({ ...popupForm, button_link: e.target.value })} placeholder="Ex: /catalogo ou https://..." className="bg-slate-700/50 border-slate-600 text-white" />
                        <p className="text-slate-500 text-xs mt-1">Deixe vazio se não quiser redirecionar</p>
                      </div>
                      <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-blue-500 to-blue-600">
                        <Save size={18} className="mr-2" /> {loading ? 'Salvando...' : 'Salvar Popup'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-white mb-6">Preview</h3>
                    <div className="bg-black/60 p-8 rounded-xl flex items-center justify-center min-h-[350px]">
                      <div className="relative">
                        {popupForm.image ? (
                          <>
                            <img src={popupForm.image} alt="Popup" className="max-w-sm rounded-2xl shadow-2xl cursor-pointer" />
                            <button className="absolute -top-3 -right-3 bg-white p-2 rounded-full shadow-lg">
                              <X size={20} className="text-gray-700" />
                            </button>
                          </>
                        ) : (
                          <div className="bg-slate-700/50 border-2 border-dashed border-slate-600 rounded-2xl p-12 text-center">
                            <ImageIcon size={48} className="mx-auto text-slate-500 mb-4" />
                            <p className="text-slate-400">Faça upload de uma imagem</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* SETTINGS - Parceiro Destaque */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">Parceiro em Destaque</h2>
                <p className="text-slate-400">Edite a seção do parceiro que aparece na página inicial</p>
              </div>

              <Tip>
                Esta seção aparece na Home do site com a logo grande do parceiro, título, descrição e botão de ação.
                Você pode configurar qualquer marca parceira para aparecer em destaque.
              </Tip>

              <form onSubmit={saveSiteSettings} className="space-y-8">
                {/* Seção Principal do Parceiro */}
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                      <Sparkles size={20} className="text-red-400" />
                      Informações do Parceiro
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Logo do Parceiro *</label>
                          <Input 
                            type="file" 
                            accept="image/png,image/svg+xml,image/jpeg" 
                            onChange={(e) => handleImageUpload(e, 'settings')} 
                            className="bg-slate-700/50 border-slate-600 text-white file:bg-slate-600 file:text-white file:border-0" 
                          />
                          <div className="mt-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <p className="text-xs text-red-200">
                              📐 Tamanho ideal: <span className="font-mono bg-slate-700/50 px-1.5 py-0.5 rounded">400 x 150 px</span> | 
                              PNG/SVG com fundo transparente | Max 2MB
                            </p>
                          </div>
                          {settingsForm.partner_section_logo && (
                            <div className="mt-3 flex items-center gap-4 p-4 bg-slate-700/30 rounded-xl">
                              <img src={settingsForm.partner_section_logo} alt="Preview" className="h-16 w-auto object-contain bg-white p-2 rounded-lg" />
                              <div className="flex flex-col gap-2">
                                <Input 
                                  value={settingsForm.partner_section_logo}
                                  onChange={(e) => setSettingsForm({ ...settingsForm, partner_section_logo: e.target.value })}
                                  placeholder="Ou cole a URL da logo"
                                  className="bg-slate-700/50 border-slate-600 text-white text-xs"
                                />
                              </div>
                            </div>
                          )}
                          {!settingsForm.partner_section_logo && (
                            <Input 
                              value={settingsForm.partner_section_logo || ''}
                              onChange={(e) => setSettingsForm({ ...settingsForm, partner_section_logo: e.target.value })}
                              placeholder="Ou cole a URL da logo (ex: https://cdn.../logo.svg)"
                              className="mt-2 bg-slate-700/50 border-slate-600 text-white"
                            />
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Título *</label>
                          <Input 
                            value={settingsForm.partner_section_title} 
                            onChange={(e) => setSettingsForm({ ...settingsForm, partner_section_title: e.target.value })} 
                            placeholder="Ex: Distribuidor Autorizado"
                            className="bg-slate-700/50 border-slate-600 text-white" 
                            required 
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Nome da Marca *</label>
                          <Input 
                            value={settingsForm.partner_section_brand} 
                            onChange={(e) => setSettingsForm({ ...settingsForm, partner_section_brand: e.target.value })} 
                            placeholder="Ex: STIHL"
                            className="bg-slate-700/50 border-slate-600 text-white" 
                            required 
                          />
                          <p className="text-slate-500 text-xs mt-1">Aparece em destaque abaixo do título</p>
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Descrição</label>
                          <Textarea 
                            value={settingsForm.partner_section_description} 
                            onChange={(e) => setSettingsForm({ ...settingsForm, partner_section_description: e.target.value })} 
                            placeholder="Descreva a parceria com a marca..."
                            className="bg-slate-700/50 border-slate-600 text-white min-h-[120px]" 
                          />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">Texto do Botão</label>
                            <Input 
                              value={settingsForm.partner_section_button_text} 
                              onChange={(e) => setSettingsForm({ ...settingsForm, partner_section_button_text: e.target.value })} 
                              placeholder="Ver Produtos STIHL"
                              className="bg-slate-700/50 border-slate-600 text-white" 
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">Link do Botão</label>
                            <Input 
                              value={settingsForm.partner_section_button_link} 
                              onChange={(e) => setSettingsForm({ ...settingsForm, partner_section_button_link: e.target.value })} 
                              placeholder="/catalogo?marca=STIHL"
                              className="bg-slate-700/50 border-slate-600 text-white" 
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Indicadores do Parceiro */}
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-white mb-6">Indicadores (Cards abaixo da descrição)</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="p-4 bg-slate-700/30 rounded-xl">
                        <label className="block text-sm font-semibold text-red-400 mb-3">Indicador 1</label>
                        <div className="space-y-3">
                          <Input 
                            value={settingsForm.partner_section_indicator1_title} 
                            onChange={(e) => setSettingsForm({ ...settingsForm, partner_section_indicator1_title: e.target.value })} 
                            placeholder="100%"
                            className="bg-slate-700/50 border-slate-600 text-white" 
                          />
                          <Input 
                            value={settingsForm.partner_section_indicator1_subtitle} 
                            onChange={(e) => setSettingsForm({ ...settingsForm, partner_section_indicator1_subtitle: e.target.value })} 
                            placeholder="Produtos Originais"
                            className="bg-slate-700/50 border-slate-600 text-white" 
                          />
                        </div>
                      </div>
                      
                      <div className="p-4 bg-slate-700/30 rounded-xl">
                        <label className="block text-sm font-semibold text-red-400 mb-3">Indicador 2</label>
                        <div className="space-y-3">
                          <Input 
                            value={settingsForm.partner_section_indicator2_title} 
                            onChange={(e) => setSettingsForm({ ...settingsForm, partner_section_indicator2_title: e.target.value })} 
                            placeholder="Garantia"
                            className="bg-slate-700/50 border-slate-600 text-white" 
                          />
                          <Input 
                            value={settingsForm.partner_section_indicator2_subtitle} 
                            onChange={(e) => setSettingsForm({ ...settingsForm, partner_section_indicator2_subtitle: e.target.value })} 
                            placeholder="Oficial de Fábrica"
                            className="bg-slate-700/50 border-slate-600 text-white" 
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Preview */}
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-white mb-6">Preview da Seção</h3>
                    <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 rounded-xl">
                      <div className="grid md:grid-cols-2 gap-8 items-center max-w-4xl mx-auto">
                        <div className="text-center">
                          <div className="bg-white/95 p-8 rounded-2xl inline-block">
                            <img 
                              src={settingsForm.partner_section_logo || 'https://cdn.worldvectorlogo.com/logos/stihl.svg'} 
                              alt="Preview" 
                              className="max-w-[200px] mx-auto"
                            />
                          </div>
                        </div>
                        <div className="text-white">
                          <h4 className="text-2xl font-black mb-2">
                            {settingsForm.partner_section_title || 'Distribuidor Autorizado'}
                            <span className="block text-orange-400">{settingsForm.partner_section_brand || 'STIHL'}</span>
                          </h4>
                          <p className="text-gray-300 text-sm mb-4 line-clamp-2">
                            {settingsForm.partner_section_description || 'Descrição da parceria...'}
                          </p>
                          <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="bg-white/10 rounded-lg p-3">
                              <div className="text-lg font-black text-orange-400">{settingsForm.partner_section_indicator1_title || '100%'}</div>
                              <div className="text-xs">{settingsForm.partner_section_indicator1_subtitle || 'Produtos Originais'}</div>
                            </div>
                            <div className="bg-white/10 rounded-lg p-3">
                              <div className="text-lg font-black text-orange-400">{settingsForm.partner_section_indicator2_title || 'Garantia'}</div>
                              <div className="text-xs">{settingsForm.partner_section_indicator2_subtitle || 'De Fábrica'}</div>
                            </div>
                          </div>
                          <button className="bg-orange-600 text-white font-bold py-2 px-6 rounded-lg text-sm">
                            {settingsForm.partner_section_button_text || 'Ver Produtos'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold py-6">
                  <Save size={18} className="mr-2" /> {loading ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
